import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import '../styles/register.css';
import Notification from '../components/Notification';
import Cookies from 'js-cookie';
import AvatarEditor from 'react-avatar-editor';
import Webcam from 'react-webcam'; // Add this import
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ProfileSkeleton } from '../components/Skeletons';

import {
    faUser,
    faEnvelope,
    faVenusMars,
    faCalendarAlt,
    faCity,
    faPhone,
    faCamera,
    faTrash,
    faImage,
    faCameraRotate,
    faTimes
} from '@fortawesome/free-solid-svg-icons';
import { UserContext } from '../contexts/UserContext';

const Profile = ({ onLoginClick }) => {
    const { user, setUser } = useContext(UserContext);
    const [profile, setProfile] = useState({
        id: '',
        username: '',
        email: '',
        sex: '',
        date_of_birth: '',
        first_name: '',
        last_name: '',
        profile_picture: null,
        profile_picture_url: '',
        mobile_number: '',
        city: '',
        company: '',
        referred_categories: [],
        preferred_cities: [],
        trusted_sellers: [],
        notification_preferences: {
            email_notifications: true,
            push_notifications: true,
            sms_notifications: true,
            new_follower_alerts: true,
            product_updates: true,
            price_alerts: true,
            message_notifications: true
        }
    });
    const [profilePictureName, setProfilePictureName] = useState('');
    const [profilePictureFile, setProfilePictureFile] = useState(null);
    const [removeProfilePicture, setRemoveProfilePicture] = useState(false); // New state to track removal
    const [originalEmail, setOriginalEmail] = useState(''); // Track original email
    const [cities, setCities] = useState([]);
    const [notification, setNotification] = useState({ message: '', type: '', header: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [errors, setErrors] = useState({});
    const [showCamera, setShowCamera] = useState(false);
    const [isFrontCamera, setIsFrontCamera] = useState(true);
    const webcamRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [lightLevel, setLightLevel] = useState('good');
    const editorRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const token = Cookies.get('authToken');
        if (!token) {
            onLoginClick();
            return;
        }

        const fetchProfile = async () => {
            try {
                const response = await fetch(`${config.backendUrl}/users/me/`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    setProfile(data);
                    setOriginalEmail(data.email || ''); // Store original email
                    // Handle both profile_picture and profile_picture_url from API
                    const pictureUrl = data.profile_picture || data.profile_picture_url;
                    if (pictureUrl) {
                        // Only set the display name, not the file (profilePictureFile should only be set when user selects a new file)
                        setProfilePictureName(pictureUrl.split('/').pop());
                    }
                } else {
                    console.error('Failed to fetch profile');
                }
            } catch (error) {
                console.error('An error occurred while fetching profile:', error);
            }
        };

        const fetchCities = async () => {
            try {
                const response = await fetch(`${config.backendUrl}/users/cities/`);
                if (response.ok) {
                    const data = await response.json();
                    setCities(data);
                } else {
                    console.error('Failed to fetch cities');
                }
            } catch (error) {
                console.error('An error occurred while fetching cities:', error);
            }
        };
        const fetchData = async () => {
            setLoading(true);
            await Promise.all([fetchProfile(), fetchCities()]);
            setLoading(false);
        };

        fetchData();
    }, [onLoginClick]);

    const validateForm = () => {
        const newErrors = {};
        if (!profile.username) newErrors.username = "Le nom d'utilisateur est requis";
        
        // Email is optional, but if provided, must be valid and not exist
        if (profile.email) {
            if (!/^\S+@\S+\.\S+$/.test(profile.email)) {
                newErrors.email = 'Email invalide';
            } else if (errors.email) {
                // If there's already an error (like "email exists"), keep it
                newErrors.email = errors.email;
            }
        }
        
        if (!profile.first_name) newErrors.first_name = 'Le prénom est requis';
        if (!profile.last_name) newErrors.last_name = 'Le nom de famille est requis';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleCapture = React.useCallback(() => {
        const imageSrc = webcamRef.current.getScreenshot();
        if (imageSrc) {
            fetch(imageSrc)
                .then(res => res.blob())
                .then(blob => {
                    const file = new File([blob], "camera-photo.png", { type: "image/png" });
                    setProfile(prev => ({
                        ...prev,
                        profile_picture: file
                    }));
                    setProfilePictureFile(file);
                    setIsEditing(true);
                    setShowCamera(false);
                });
        }
    }, [webcamRef]);

    const checkLighting = () => {
        if (webcamRef.current && webcamRef.current.video) {
            const video = webcamRef.current.video;
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;
            let brightness = 0;
            for (let i = 0; i < data.length; i += 4) {
                brightness += (data[i] + data[i + 1] + data[i + 2]) / 3;
            }
            brightness = brightness / (data.length / 4);

            if (brightness < 50) setLightLevel('low');
            else if (brightness > 200) setLightLevel('high');
            else setLightLevel('good');
        }
    };

    const handleChange = async (e) => {
        const { name, value, files } = e.target;

        if (name === 'profile_picture') {
            if (files.length > 0) {
                setProfilePictureFile(files[0]);
                setProfilePictureName(files[0].name);
                setIsEditing(true);
                setRemoveProfilePicture(false); // Reset the removal flag
            }
        } else if (name === 'first_name' || name === 'last_name') {
            if (!/^[a-zA-Z]*$/.test(value)) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    [name]: 'Ce champ ne doit contenir que des lettres.'
                }));
                return;
            } else {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    [name]: ''
                }));
            }

            setProfile({
                ...profile,
                [name]: value
            });

            if (name === 'first_name' && value === profile.last_name) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    first_name: 'Le prénom et le nom de famille ne peuvent pas être identiques.',
                    last_name: 'Le prénom et le nom de famille ne peuvent pas être identiques.'
                }));
            } else if (name === 'last_name' && value === profile.first_name) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    first_name: 'Le prénom et le nom de famille ne peuvent pas être identiques.',
                    last_name: 'Le prénom et le nom de famille ne peuvent pas être identiques.'
                }));
            } else {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    first_name: '',
                    last_name: ''
                }));
            }
        } else if (name === 'sex') {
            if (!['homme', 'femme'].includes(value)) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    sex: 'Sexe invalide. Veuillez choisir "Homme" ou "Femme".'
                }));
            } else {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    sex: ''
                }));
                setProfile({
                    ...profile,
                    sex: value
                });
            }
        } else if (name === 'date_of_birth') {
            const birthDate = new Date(value);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const isBirthdayPassed = (today.getMonth() > birthDate.getMonth()) ||
                (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());
            if (!isBirthdayPassed) age--; // Adjust age if birthday hasn't passed yet this year.

            if (age < 18 || age > 89) {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    date_of_birth: 'Votre âge doit être compris entre 18 et 89 ans'
                }));
            } else {
                setErrors(prevErrors => ({
                    ...prevErrors,
                    date_of_birth: ''
                }));
                setProfile({
                    ...profile,
                    date_of_birth: value
                });
            }
        } else {
            setProfile({
                ...profile,
                [name]: value
            });
        }

        if (name === 'username' && value) {
            const usernameValidation = /^(?!.*__.*)(?!.*\.\.)(?!.*\.$)[^\W][\w.]{2,19}$/;
            if (!usernameValidation.test(value)) {
                setErrors(prevErrors => ({ ...prevErrors, username: "Le nom d'utilisateur doit être entre 3 et 20 caractères, ne peut contenir que des lettres, des chiffres et des underscores, et ne peut pas commencer ou se terminer par un underscore ou contenir des underscores consécutifs." }));
            } else {
                try {
                    const response = await axios.post(`${config.backendUrl}/users/check-username/`, { username: value });
                    if (response.data.exists) {
                        setErrors(prevErrors => ({ ...prevErrors, username: "Le nom d'utilisateur existe déjà" }));
                    } else {
                        setErrors(prevErrors => {
                            const { username, ...rest } = prevErrors;
                            return rest;
                        });
                    }
                } catch (error) {
                    console.error('Erreur lors de la vérification du nom d\'utilisateur:', error);
                }
            }
        }

        if (name === 'email' && value) {
            if (!/^\S+@\S+\.\S+$/.test(value)) {
                setErrors(prevErrors => ({ ...prevErrors, email: 'Email invalide' }));
            } else if (value === originalEmail) {
                // Email hasn't changed, clear any errors
                setErrors(prevErrors => {
                    const { email, ...rest } = prevErrors;
                    return rest;
                });
            } else {
                // Email has changed, check if it exists
                try {
                    const response = await axios.post(`${config.backendUrl}/users/check-email/`, { email: value });
                    if (response.data.exists) {
                        setErrors(prevErrors => ({ ...prevErrors, email: "L'email existe déjà" }));
                    } else {
                        setErrors(prevErrors => {
                            const { email, ...rest } = prevErrors;
                            return rest;
                        });
                    }
                } catch (error) {
                    console.error('Erreur lors de la vérification de l\'email:', error);
                }
            }
        }
    };
    const handleSave = async () => {
        if (editorRef.current) {
            try {
                const canvas = editorRef.current.getImageScaledToCanvas();
                const dataUrl = canvas.toDataURL('image/png');
                const blob = await fetch(dataUrl).then(res => res.blob());
                const file = new File([blob], profilePictureName || 'profile-image.png', { type: 'image/png' });
                const newImageUrl = URL.createObjectURL(blob);

                // Clean up old URLs first
                if (profile.profile_picture_url && profile.profile_picture_url.startsWith('blob:')) {
                    URL.revokeObjectURL(profile.profile_picture_url);
                }

                // Update state with new image data
                setProfile(prevProfile => ({
                    ...prevProfile,
                    profile_picture: file,
                    profile_picture_url: newImageUrl
                }));
                setProfilePictureFile(file);
                setIsEditing(false);
                setRemoveProfilePicture(false);
            } catch (error) {
                console.error('Error saving image:', error);
                setNotification({
                    message: 'Erreur lors de la sauvegarde de l\'image',
                    type: 'error',
                    header: 'Erreur'
                });
            }
        }
    };

    useEffect(() => {
        return () => {
            // Cleanup blob URLs when component unmounts
            if (profile.profile_picture_url && profile.profile_picture_url.startsWith('blob:')) {
                URL.revokeObjectURL(profile.profile_picture_url);
            }
        };
    }, [profile.profile_picture_url]);

    const handleRemoveImage = (e) => {
        e.stopPropagation(); // Prevent the click event from propagating to the parent div
        setProfile({ ...profile, profile_picture: null, profile_picture_url: '' });
        setProfilePictureFile(null);
        setProfilePictureName('');
        setRemoveProfilePicture(true); // Set the removal flag
    };
    const getProfileImageUrl = (url) => {
        if (!url) return null;

        // If it's already a full URL (including http or https), return it directly
        if (url.startsWith('http')) {
            return url;
        }

        // For relative URLs, append the backend URL
        return `${config.backendUrl.replace(/\/$/, '')}${url}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSubmitting(true);
        const formData = new FormData();

        // Handle basic fields
        const basicFields = ['username', 'email', 'first_name', 'last_name', 'mobile_number'];
        basicFields.forEach(field => {
            if (profile[field] !== null) {
                formData.append(field, profile[field]);
            }
        });

        // Handle city (already an ID)
        if (profile.city) {
            formData.append('city', profile.city);
        }

        // Handle array fields - ensure they are arrays of integers
        if (profile.referred_categories && profile.referred_categories.length > 0) {
            const categoryIds = profile.referred_categories.map(cat =>
                typeof cat === 'object' ? cat.id : parseInt(cat, 10)
            ).filter(id => !isNaN(id));
            formData.append('referred_categories', JSON.stringify(categoryIds));
        }

        if (profile.preferred_cities && profile.preferred_cities.length > 0) {
            const cityIds = profile.preferred_cities.map(city =>
                typeof city === 'object' ? city.id : parseInt(city, 10)
            ).filter(id => !isNaN(id));
            formData.append('preferred_cities', JSON.stringify(cityIds));
        }

        if (profile.trusted_sellers && profile.trusted_sellers.length > 0) {
            const sellerIds = profile.trusted_sellers.map(seller =>
                typeof seller === 'object' ? seller.id : parseInt(seller, 10)
            ).filter(id => !isNaN(id));
            formData.append('trusted_sellers', JSON.stringify(sellerIds));
        }

        // Handle notification preferences
        if (profile.notification_preferences) {
            formData.append('notification_preferences', JSON.stringify(
                profile.notification_preferences
            ));
        }

        // Handle profile picture
        if (profilePictureFile) {
            formData.append('profile_picture', profilePictureFile);
        }

        // Handle profile picture removal
        if (removeProfilePicture) {
            formData.append('remove_profile_picture', 'true');
        }
        // Handle sex
        if (profile.sex) { // Appends if profile.sex is a non-empty string like "homme" or "Femme"
            formData.append('sex', profile.sex);
        }

        // Handle date_of_birth
        if (profile.date_of_birth) { // Appends if profile.date_of_birth is "YYYY-MM-DD"
            formData.append('date_of_birth', profile.date_of_birth);
        } else if (profile.hasOwnProperty('date_of_birth') && profile.date_of_birth === '') {
            // If user explicitly cleared the date field, it becomes "" in profile state.
            // Send empty string; backend will convert to None.
            formData.append('date_of_birth', '');
        }

        const token = Cookies.get('authToken');

        try {
            const response = await fetch(`${config.backendUrl}/users/edit/${profile.uuid || profile.id}/`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Token ${token}`
                },
                body: formData,
            });

            if (response.ok) {
                const data = await response.json();
                setProfile(data);
                setUser(data);
                setNotification({
                    message: 'Profil mis à jour avec succès.',
                    type: 'success',
                    header: 'Profile enregistré'
                });
                setTimeout(() => {
                    navigate(`/user/corner/${user.uuid || user.id}/`);  // Use UUID if available
                }, 2000);
            } else {
                const errorData = await response.json();
                console.error('Server response:', errorData);  // Add this for debugging
                
                // Check for content moderation rejection
                const errorString = typeof errorData === 'string' ? errorData : JSON.stringify(errorData);
                if (errorString.includes('inapproprié') || errorString.includes('inappropriate')) {
                    setNotification({
                        message: errorData.error || errorData.detail || errorData.message || 
                                'Cette photo de profil contient du contenu inapproprié et ne peut pas être téléchargée. Veuillez choisir une image appropriée.',
                        type: 'error',
                        header: '⚠️ Image rejetée'
                    });
                } else {
                    setNotification({
                        message: errorData.error || 'Une erreur est survenue. Veuillez réessayer.',
                        type: 'error',
                        header: 'Mise à jour échouée'
                    });
                }
            }
        } catch (error) {
            console.error('Error updating profile:', error);
            setNotification({
                message: 'Une erreur est survenue. Veuillez réessayer.',
                type: 'error',
                header: 'Mise à jour échouée'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleNotificationClose = () => {
        setNotification({ message: '', type: '', header: '' });
    };

    if (loading) {
        return (
            <div className="profile-container">
                <ProfileSkeleton /> {/* Skeleton for the profile form */}
            </div>
        );
    }

    return (
        <div className="register-container">
            <Notification
                message={notification.message}
                type={notification.type}
                header={notification.header}
                onClose={handleNotificationClose}
            />
            <div className="register-card">
                <form className="register-form" onSubmit={handleSubmit} encType="multipart/form-data">
                    <div className="profile-pic-container">
                        {showCamera ? (
                            <div className="camera-container-fullscreen">
                                <div className="camera-overlay">
                                    <div className="camera-guide-circle"></div>
                                    <div className="camera-instructions">
                                        <p>Positionnez votre visage dans le cercle</p>
                                        {lightLevel !== 'good' && (
                                            <p className={`lighting-warning ${lightLevel}`}>
                                                {lightLevel === 'low' ?
                                                    '⚠️ Éclairage faible. Déplacez-vous vers un endroit plus lumineux.' :
                                                    '⚠️ Éclairage trop fort. Évitez la lumière directe.'}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <Webcam
                                    ref={webcamRef}
                                    audio={false}
                                    screenshotFormat="image/png"
                                    videoConstraints={{
                                        facingMode: isFrontCamera ? 'user' : 'environment',
                                        width: { min: 1280, ideal: 1920, max: 2560 },
                                        height: { min: 720, ideal: 1080, max: 1440 },
                                    }}
                                    onUserMedia={() => setInterval(checkLighting, 1000)}
                                />
                                <div className="camera-controls">
                                    <button type="button" className="camera-btn" onClick={() => setIsFrontCamera(!isFrontCamera)}>
                                        <FontAwesomeIcon icon={faCameraRotate} />
                                    </button>
                                    <button type="button" className="camera-btn capture-btn" onClick={handleCapture}>
                                        <div className="capture-btn-inner"></div>
                                    </button>
                                    <button type="button" className="camera-btn" onClick={() => setShowCamera(false)}>
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="profile-image-container">
                                    {profile.profile_picture_url && !isEditing ? (
                                        <img
                                            src={profile.profile_picture_url.startsWith('blob:') ? profile.profile_picture_url : getProfileImageUrl(profile.profile_picture_url)}
                                            alt="Profile"
                                            className="profile-picture-image"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.style.display = 'none';
                                                const initialsDiv = document.createElement('div');
                                                initialsDiv.className = 'profile-initials';
                                                initialsDiv.innerText = `${profile.first_name.charAt(0)}${profile.last_name.charAt(0)}`;
                                                e.target.parentNode.appendChild(initialsDiv);
                                            }}
                                        />
                                    ) : profilePictureFile && isEditing ? (
                                        <div className="avatar-editor-container">
                                            <AvatarEditor
                                                ref={editorRef}
                                                image={profilePictureFile}
                                                width={250}
                                                height={250}
                                                border={0}
                                                borderRadius={125}
                                                scale={1.2}
                                                rotate={0}
                                                position={{ x: 0.5, y: 0.5 }}
                                                className="avatar-editor"
                                            />
                                        </div>
                                    ) : (
                                        <div className="profile-initials">
                                            {profile.first_name.charAt(0)}{profile.last_name.charAt(0)}
                                        </div>
                                    )}
                                    {profile.profile_picture_url && !isEditing && (
                                        <FontAwesomeIcon
                                            icon={faTrash}
                                            className="trash-icon"
                                            onClick={handleRemoveImage}
                                            title="Supprimer la photo"
                                        />
                                    )}
                                </div>
                                <div className="profile-picture-options">
                                    <button
                                        type="button"
                                        className="picture-option-btn"
                                        onClick={() => setShowCamera(true)}
                                    >
                                        <FontAwesomeIcon icon={faCamera} />
                                        <span>Prendre une photo</span>
                                    </button>
                                    <div className="separator">ou</div>
                                    <label className="picture-option-btn">
                                        <FontAwesomeIcon icon={faImage} />
                                        <span>Choisir une image</span>
                                        <input
                                            type="file"
                                            id="profile_picture"
                                            name="profile_picture"
                                            accept="image/*"
                                            onChange={handleChange}
                                            style={{ display: 'none' }}
                                        />
                                    </label>
                                </div>
                            </>
                        )}
                    </div>
                    {isEditing && (
                        <button type="button" onClick={handleSave} className="verify-button">
                            Enregistrer l'image
                        </button>
                    )}
                    <div className="input-group">
                        <label>Nom d'utilisateur</label>
                        <div className={`input-container ${errors.username ? 'input-container-error' : ''} ${!errors.username && profile.username ? 'input-container-valid' : ''}`}>
                            <FontAwesomeIcon icon={faUser} className={`icon ${errors.username ? 'icon-error' : ''} ${!errors.username && profile.username ? 'icon-valid' : ''}`} />
                            <input
                                type="text"
                                name="username"
                                value={profile.username}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        {errors.username && <p className="error-message">{errors.username}</p>}
                    </div>
                    <div className="input-group">
                        <label>Email (optionnel)</label>
                        <div className={`input-container ${errors.email ? 'input-container-error' : ''} ${!errors.email && profile.email ? 'input-container-valid' : ''}`}>
                            <FontAwesomeIcon icon={faEnvelope} className={`icon ${errors.email ? 'icon-error' : ''} ${!errors.email && profile.email ? 'icon-valid' : ''}`} />
                            <input
                                type="email"
                                name="email"
                                value={profile.email || ''}
                                onChange={handleChange}
                                placeholder="votre@email.com"
                            />
                        </div>
                        {errors.email && <p className="error-message">{errors.email}</p>}
                    </div>
                    <div className="input-group">
                        <label>Sexe</label>
                        <div className={`input-container ${errors.sex ? 'input-container-error' : ''} ${!errors.sex && profile.sex ? 'input-container-valid' : ''}`}>
                            <FontAwesomeIcon icon={faVenusMars} className={`icon ${errors.sex ? 'icon-error' : ''} ${!errors.sex && profile.sex ? 'icon-valid' : ''}`} />
                            <select
                                name="sex"
                                value={profile.sex || ''}
                                onChange={handleChange}
                                className="dropdown"
                            >
                                <option value="">Sélectionner le sexe</option>
                                <option value="homme">Homme</option>
                                <option value="femme">Femme</option>
                            </select>
                        </div>
                        {errors.sex && <p className="error-message">{errors.sex}</p>}
                    </div>

                    <div className="input-group">
                        <label>Date de naissance</label>
                        <div className={`input-container ${errors.date_of_birth ? 'input-container-error' : ''} ${!errors.date_of_birth && profile.date_of_birth ? 'input-container-valid' : ''}`}>
                            <FontAwesomeIcon icon={faCalendarAlt} className={`icon ${errors.date_of_birth ? 'icon-error' : ''} ${!errors.date_of_birth && profile.date_of_birth ? 'icon-valid' : ''}`} />
                            <input
                                type="date"
                                name="date_of_birth"
                                value={profile.date_of_birth || ''}
                                onChange={handleChange}
                                min={new Date(new Date().setFullYear(new Date().getFullYear() - 89)).toISOString().split('T')[0]} // Maximum age: 89 years
                                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]} // Minimum age: 18 years
                            />
                        </div>
                        {errors.date_of_birth && <p className="error-message">{errors.date_of_birth}</p>}
                    </div>
                    <div className="input-group">
                        <label>Prénom</label>
                        <div className={`input-container ${errors.first_name ? 'input-container-error' : ''} ${!errors.first_name && profile.first_name ? 'input-container-valid' : ''}`}>
                            <FontAwesomeIcon icon={faUser} className={`icon ${errors.first_name ? 'icon-error' : ''} ${!errors.first_name && profile.first_name ? 'icon-valid' : ''}`} />
                            <input
                                type="text"
                                name="first_name"
                                value={profile.first_name}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        {errors.first_name && <p className="error-message">{errors.first_name}</p>}
                    </div>
                    <div className="input-group">
                        <label>Nom de famille</label>
                        <div className={`input-container ${errors.last_name ? 'input-container-error' : ''} ${!errors.last_name && profile.last_name ? 'input-container-valid' : ''}`}>
                            <FontAwesomeIcon icon={faUser} className={`icon ${errors.last_name ? 'icon-error' : ''} ${!errors.last_name && profile.last_name ? 'icon-valid' : ''}`} />
                            <input
                                type="text"
                                name="last_name"
                                value={profile.last_name}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        {errors.last_name && <p className="error-message">{errors.last_name}</p>}
                    </div>
                    <div className="input-group">
                        <label>Ville</label>
                        <div className={`input-container ${errors.city ? 'input-container-error' : ''} ${!errors.city && profile.city ? 'input-container-valid' : ''}`}>
                            <FontAwesomeIcon icon={faCity} className={`icon ${errors.city ? 'icon-error' : ''} ${!errors.city && profile.city ? 'icon-valid' : ''}`} />
                            <select
                                name="city"
                                value={profile.city || ''}
                                onChange={handleChange}
                                className="dropdown"
                            >
                                <option value="">Sélectionner une ville</option>
                                {cities.map(city => (
                                    <option key={city.id} value={city.id}>
                                        {city.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {errors.city && <p className="error-message">{errors.city}</p>}
                    </div>
                    <div className="input-group">
                        <label>Numéro de téléphone</label>
                        <div className={`input-container ${errors.mobile_number ? 'input-container-error' : ''} ${!errors.mobile_number && profile.mobile_number ? 'input-container-valid' : ''}`}>
                            <FontAwesomeIcon icon={faPhone} className={`icon ${errors.mobile_number ? 'icon-error' : ''} ${!errors.mobile_number && profile.mobile_number ? 'icon-valid' : ''}`} />
                            <input
                                type="tel"
                                name="mobile_number"
                                value={profile.mobile_number || ''}
                                onChange={handleChange}
                                placeholder="+243 XXX XXX XXX"
                                disabled
                                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                                title="Le numéro de téléphone ne peut pas être modifié"
                            />
                        </div>
                        <p className="helper-text" style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            Le numéro de téléphone est utilisé pour la vérification et ne peut pas être modifié.
                        </p>
                    </div>
                    <div className="buttons">
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={() => navigate(-1)}
                        >
                            Retour
                        </button>
                        <button
                            type="submit"
                            className="register-button"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Mise à jour en cours...' : 'Mettre à jour le profil'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Profile;